
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useOrganization } from "@/contexts/OrganizationContext";
import { setCurrentSupabaseOrganization } from "@/lib/supabaseOrganization";

export interface FormattedInfluencer {
  id: string;
  name: string;
  first_name: string;
  handle: string;
  email: string;
  phone: string;
  platform: string;
  avatar: string;
  profilePicUrl?: string | null;
  engagement: number;
  engagement_rate: string;
  reach: string;
  campaigns: number;
  avgFee: string;
  avgROI: string;
  location: string;
  isVerified: boolean;
  followers: string;
  followers_count: string;
  rate: number;
  tags: string[];
  brands: string[];
  thumbnails: string[];
  aiScore: number | null;
  confidence: string;
  tier: string | null;
  platforms: string[];
}

type InfluencerRecord = Pick<
  Database['public']['Tables']['influencers']['Row'],
  | 'id'
  | 'name'
  | 'handle'
  | 'email'
  | 'phone'
  | 'platform'
  | 'profile_pic_url'
  | 'engagement_rate'
  | 'followers_count'
  | 'rate_per_post'
  | 'location'
  | 'verified'
  | 'categories'
  | 'tier'
>;

const formatCompactNumber = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(value);
};

const formatCurrency = (value: number | null): string => {
  if (value === null || value === 0) return '-';
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value)}`;
};

export function useAllInfluencers() {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;

  return useQuery({
    queryKey: ['all-influencers', orgId],
    queryFn: async (): Promise<FormattedInfluencer[]> => {

      if (!orgId) return [];
      await setCurrentSupabaseOrganization(orgId);

      const { data: influencers, error: influencersError } = await supabase
        .from('influencers')
        .select('id, name, handle, email, phone, platform, profile_pic_url, engagement_rate, followers_count, rate_per_post, location, verified, categories, tier')
        .eq('organization_id', orgId);

      if (influencersError) throw influencersError;

      if (!influencers || influencers.length === 0) {
        return [];
      }

      const influencerRecords = influencers as InfluencerRecord[];
      const influencerIds = influencerRecords.map((influencer) => influencer.id);
      const { data: assignments, error: assignmentsError } = await supabase
        .from('campaign_influencers')
        .select('influencer_id')
        .in('influencer_id', influencerIds);

      if (assignmentsError) throw assignmentsError;

      const campaignCounts = new Map<string, number>();
      for (const assignment of assignments ?? []) {
        campaignCounts.set(
          assignment.influencer_id,
          (campaignCounts.get(assignment.influencer_id) ?? 0) + 1,
        );
      }

      return influencerRecords.map((influencer) => {
        const fullName = influencer.name || '';
        const firstName = fullName.split(' ')[0] || fullName;
        const engagement = Number(influencer.engagement_rate) || 0;
        const followersCount = Number(influencer.followers_count) || 0;
        const profilePicUrl = influencer.profile_pic_url || null;

        return {
          id: influencer.id,
          name: fullName,
          first_name: firstName,
          handle: influencer.handle || '',
          email: influencer.email || '',
          phone: influencer.phone || '',
          platform: influencer.platform,
          avatar: profilePicUrl || '',
          profilePicUrl,
          engagement,
          engagement_rate: engagement ? `${engagement.toFixed(2)}%` : '',
          reach: formatCompactNumber(followersCount),
          campaigns: campaignCounts.get(influencer.id) ?? 0,
          avgFee: formatCurrency(influencer.rate_per_post),
          avgROI: '-',
          location: influencer.location || '',
          isVerified: influencer.verified ?? false,
          followers: formatCompactNumber(followersCount),
          followers_count: String(followersCount),
          rate: Number(influencer.rate_per_post) || 0,
          tags: influencer.categories || [],
          brands: [],
          thumbnails: profilePicUrl ? [profilePicUrl] : [],
          aiScore: null,
          confidence: "High",
          tier: influencer.tier,
          platforms: [influencer.platform],
        };
      });
    },
    enabled: !!orgId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
